/* eslint-disable no-unused-vars */
import Vue from "vue";
import { jwtDecode } from "jwt-decode";
import Cookies from "js-cookie";
//import user from "/lambda/largefiler-dashboard-service/models/User.js";

import {
  PublicClientApplication,
  LogLevel,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";

// MSAL/Azure AD B2C configuration
const authorityDomain = process.env.VUE_APP_AUTHORITY_DOMAIN;
const signInPolicy = process.env.VUE_APP_SIGNIN_POLICY;
const passwordResetPolicy = process.env.VUE_APP_PASSWORD_RESET_POLICY;
const clientId = process.env.VUE_APP_CLIENT_ID;
const tenant = process.env.VUE_APP_TENANT;
const baseURL = process.env.BASE_URL;
const apiBaseURL = process.env.VUE_APP_API_BASE_URL;

const tokenAPIScopes = [
  process.env.VUE_APP_TOKEN_API_SCOPES,
];
const loginScopes = ["openid", ...tokenAPIScopes];

const msalConfig = {
  auth: {
    clientId,
    authority: `https://${authorityDomain}/${tenant}/${signInPolicy}`,
    knownAuthorities: [authorityDomain],
    navigateToLoginRequestUrl: false,
    // postLogoutRedirectUri: "https://d2pa0nup2uah66.cloudfront.net/login", //@Mark replace with env variables here
    // redirectUri: "https://d2pa0nup2uah66.cloudfront.net/login",    
    postLogoutRedirectUri: process.env.VUE_APP_REDIRECT_URL,
    redirectUri: process.env.VUE_APP_REDIRECT_URL,
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
        case LogLevel.Error:
          console.error(message);
          break;
        case LogLevel.Info:
          console.info(message);
          break;
        case LogLevel.Verbose:
          console.debug(message);
          break;
        case LogLevel.Warning:
          console.warn(message);
          break;
        }
      },
    },
  },
};

const msalInstance = new PublicClientApplication(msalConfig);
let msalReady = false;
const initializeMSAL = async () => {
  if (msalReady) {
    return;
  }
  try {
    await msalInstance.initialize();
    msalReady = true;
    console.log("MSAL initialized");
  } catch (error) {
    console.error("MSAL initialization error:", error);
  }
};

// Helper to log token expiry time in minutes and seconds
function logTokenExpiry(refreshIn) {
  const minutes = Math.floor(refreshIn / 60000);
  const seconds = Math.floor((refreshIn % 60000) / 1000);
  console.log(
    `\x1b[32mToken will be renewed in ${minutes}m ${seconds}s\x1b[0m`,
  );
}

export const state = () => ({
  msalAccount: null,
  accessToken: "",
  refreshTimeoutId: null,
  isPasswordResetFlow: false,
  qbUserData: null, // QuickBase user data
});

export const getters = {
  // Returns user info from msalAccount and decoded accessToken, plus QB data
  userInfo: (state) => {
    // return name, given_name, family_name, email from the access token
    if (!state.accessToken) {
      return null;
    }
    const decoded = jwtDecode(state.accessToken);
    return {
      name: decoded.name,
      given_name: decoded.given_name,
      family_name: decoded.family_name,
      email: decoded.email,
      sub: decoded.sub,
      qbUser: state.qbUserData, // QuickBase user data (includes role, rid, etc.)
    };
  },
  // Returns the Azure SSO ID (sub claim from JWT) for debugging purposes
  ssoId: (state) => {
    if (!state.accessToken) {
      return null;
    }
    try {
      const decoded = jwtDecode(state.accessToken);
      return decoded.sub || null;
    } catch {
      return null;
    }
  },
  // Returns true if the user is authenticated
  isAuthenticated: (state) => !!state.accessToken && !!state.qbUserData,
};

export const mutations = {
  setAccount(state, account) {
    state.msalAccount = account;
  },
  setAccessToken(state, token) {
    //console.log("\x1b[96mSetting access token to\x1b[0m \x1b[96m%s\x1b[0m", token);
    state.accessToken = token;
  },
  clearAccount(state) {
    state.msalAccount = null;
  },
  clearAccessToken(state) {
    state.accessToken = "";
    if (state.refreshTimeoutId) {
      clearTimeout(state.refreshTimeoutId);
      state.refreshTimeoutId = null;
    }
  },
  setRefreshTimeout(state, timeoutId) {
    state.refreshTimeoutId = timeoutId;
  },
  clearRefreshTimeout(state) {
    if (state.refreshTimeoutId) {
      clearTimeout(state.refreshTimeoutId);
      state.refreshTimeoutId = null;
    }
  },
  setQBUserData(state, userData) {
    state.qbUserData = userData;
  },
  clearQBUserData(state) {
    state.qbUserData = null;
  },
};

export const actions = {
  // Start sign-in flow
  async signIn() {
    await initializeMSAL();
    const loginRequest = {
      scopes: loginScopes,
      authority: msalConfig.auth.authority,
    };
    try {
      await msalInstance.loginRedirect(loginRequest);
    } catch (error) {
      console.error("Login redirect error:", error);
    }
  },

  // Start forgot password flow
  async forgotPassword() {
    await initializeMSAL();
    const passwordResetRequest = {
      scopes: loginScopes,
      authority: `https://${authorityDomain}/${tenant}/${passwordResetPolicy}`,
    };
    try {
      await msalInstance.loginRedirect(passwordResetRequest);
    } catch (error) {
      console.error("Password reset redirect error:", error);
    }
  },

  // Handle redirect after login
  async handleRedirect({ dispatch }) {
    await initializeMSAL();
    try {
      const response = await msalInstance.handleRedirectPromise();
      if (response) {
        if (
          response.idTokenClaims?.acr?.toUpperCase() === passwordResetPolicy
        ) {
          // TODO: display a message to the user that their password has been reset
          alert("Password reset successful.");
        }

        await dispatch("getAccessToken");
        //await dispatch("attachQBUserData");
        // After getting access token, fetch QuickBase user data
        await dispatch("fetchUserFromQuickBase");
        await dispatch("scheduleTokenRefresh");
      }

      console.log("handleRedirect complete");
    } catch (error) {
      if (error?.errorMessage?.includes("AADB2C90118")) {
        await dispatch("forgotPassword");
        return;
      }

      if (error instanceof InteractionRequiredAuthError) {
        console.warn("Interaction required for authentication:", error);
        throw error; // Re-throw for component to handle
      } else {
        console.warn("Error handling redirect:", error);
        throw error; // Re-throw for component to handle
      }
    }
  },

  //Attach QuickBase user data to SSO
  async attachQBUserData({ state }) {
    if (!state.accessToken) {
      console.warn("Cannot attach QB user: No access token");
      return;
    }

    try {
      const decoded = jwtDecode(state.accessToken);
      const ssoUserId = decoded.sub;
      const email = decoded.email;

      const response = await fetch(`${apiBaseURL}/user?email=${email}`, { method: 'GET' });

      console.log("QuickBase API response:", response);

      if (response.ok) {
        const responseData = await response.json();

        const users = responseData.users;

        if (!users || users.length === 0) {
          throw new Error('NO_QB_USER_FOUND');
        }

        if (users.length > 1) {
          console.warn(`Multiple QB users found for email ${email}, using first match`);
        }

        const rid = users[0].rid;

        await fetch(`${apiBaseURL}/user/${rid}?sso_id=${ssoUserId}`, { method: 'POST' });

        console.log("Successfully linked SSO user to QuickBase record:", rid);
      } else {
        // Extract error code from Lambda API response
        const errorData = await response.json();
        const errorCode = errorData?.error?.code || 'UNKNOWN_ERROR';
        console.error("Failed to fetch user data:", response.status, response.statusText, errorCode);

        throw new Error(errorCode);
      }

    } catch (error) {
      console.error("Error connecting QuickBase user to sso:", error);
      // Re-throw to allow component to handle
      throw error;
    }
  },

  // Fetch user data from QuickBase based on SSO User ID
  async fetchUserFromQuickBase({ state, commit, dispatch }) {
    if (!state.accessToken) {
      console.warn("Cannot fetch QB user: No access token");
      return;
    }

    try {
      const decoded = jwtDecode(state.accessToken);
      const ssoUserId = decoded.sub;

      // Fetch user data from QuickBase API
      console.log("Fetching QuickBase user data for SSO UID:", ssoUserId);

      const response = await fetch(`${apiBaseURL}/user/${ssoUserId}`);

      console.log("QuickBase API response:", response);

      if (response.ok) {
        const responseData = await response.json();
        const qbUserData = responseData.qb_user;
        commit("setQBUserData", qbUserData);
        console.log("QuickBase user data set:", qbUserData);
      } else {
        // Extract error code from Lambda API response
        const errorData = await response.json();
        const errorCode = errorData?.error?.code || 'UNKNOWN_ERROR';
        console.error("User not found by SSO ID:", errorCode);
        commit("clearQBUserData");

        try {
          await dispatch("attachQBUserData");
          // Attachment succeeded - now fetch the linked user
          const retryResponse = await fetch(`${apiBaseURL}/user/${ssoUserId}`);
          if (retryResponse.ok) {
            const retryData = await retryResponse.json();
            commit("setQBUserData", retryData.qb_user);
            console.log("Successfully linked and fetched QB user");
            return; // Success! Exit without throwing
          }
        } catch (attachError) {
          console.error("Failed to attach QB user:", attachError);
        }

        throw new Error(errorCode);
      }
    } catch (error) {
      console.error("Error fetching QuickBase user data:", error);
      commit("clearQBUserData");
      // Re-throw to allow component to handle
      throw error;
    }
  },

  // Acquire access token silently, fallback to redirect if needed
  async getAccessToken({ state, commit, dispatch }) {
    await initializeMSAL();
    await dispatch("selectAccount");
    const account = state.msalAccount
      ? msalInstance.getAccount({
        homeAccountId: state.msalAccount.homeAccountId,
      })
      : null;
    if (!account) {
      console.warn("No account found for token acquisition");
      return;
    }
    const tokenRequest = {
      account,
      scopes: tokenAPIScopes,
      authority: msalConfig.auth.authority,
    };
    let accessToken = "";
    try {
      const response = await msalInstance.acquireTokenSilent(tokenRequest);
      accessToken = response.accessToken;
      console.log("Access token acquired silently");
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        try {
          await msalInstance.acquireTokenRedirect(tokenRequest);
          // Token will be handled after redirect
          return;
        } catch (interactiveError) {
          console.error(
            "Interactive token acquisition error:",
            interactiveError,
          );
        }
      } else {
        console.error("Error acquiring access token:", error);
      }
    }
    if (accessToken) {
      try {
        const decodedToken = jwtDecode(accessToken);
        const expiryDate = new Date(decodedToken.exp * 1000);
        Cookies.set("authToken", accessToken, {
          expires: expiryDate,
          path: "/",
          secure: true,
          sameSite: "Strict",
        });
        commit("setAccessToken", accessToken);
        await dispatch("scheduleTokenRefresh");
        console.log("Access token set and scheduled refresh");
      } catch (e) {
        console.warn("Failed to decode/set cookie for access token");
      }
    }
  },

  // Select the correct account from MSAL
  async selectAccount({ commit, dispatch }) {
    await initializeMSAL();
    const allAccounts = msalInstance.getAllAccounts();
    if (!allAccounts.length) {
      console.warn("No accounts found in MSAL");
      return;
    }
    // Filter for accounts matching our policy and client
    const matchingAccounts = allAccounts.filter(
      (account) =>
        account.homeAccountId &&
        account.homeAccountId
          .toUpperCase()
          .includes(signInPolicy.toUpperCase()) &&
        account.idTokenClaims &&
        account.idTokenClaims.iss &&
        account.idTokenClaims.iss
          .toUpperCase()
          .includes(authorityDomain.toUpperCase()) &&
        account.idTokenClaims.aud === clientId,
    );
    let selected = null;
    if (matchingAccounts.length === 1) {
      selected = matchingAccounts[0];
    } else if (matchingAccounts.length > 1) {
      // If all accounts are for the same user, pick the first
      if (
        matchingAccounts.every(
          (a) => a.localAccountId === matchingAccounts[0].localAccountId,
        )
      ) {
        selected = matchingAccounts[0];
      } else {
        // Multiple users detected, sign out all
        console.warn("Multiple users detected, logging out all.");
        await dispatch("signOut");
        return;
      }
    } else if (allAccounts.length === 1) {
      selected = allAccounts[0];
    }
    if (selected) {
      commit("setAccount", selected);
      console.log("Account set:", selected);
    } else {
      console.warn("No matching accounts found.");
    }
  },

  // Sign out and redirect
  async signOut({ commit }) {
    await initializeMSAL();
    Cookies.remove("authToken");
    commit("clearQBUserData");
    try {
      await msalInstance.logoutRedirect({
        postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri,
      });
      console.log("User signed out successfully");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  },

  // Schedules a token refresh timeout
  scheduleTokenRefresh({ state, commit, dispatch }) {
    if (state.refreshTimeoutId) {
      commit("clearRefreshTimeout");
    }
    if (!state.accessToken) {
      return;
    }
    let decoded;
    try {
      decoded = jwtDecode(state.accessToken);
    } catch (e) {
      return;
    }
    if (!decoded.exp) {
      return;
    }
    const now = Math.floor(Date.now() / 1000);
    const refreshIn = (decoded.exp - 120 - now) * 1000; // ms until 120s before expiry
    if (refreshIn <= 0) {
      // Already expired or within 2 minutes, refresh immediately
      dispatch("getAccessToken");
      return;
    }
    logTokenExpiry(refreshIn);
    commit(
      "setRefreshTimeout",
      setTimeout(() => {
        dispatch("getAccessToken");
      }, refreshIn),
    );
  },

  async loadTokenFromStorage({ commit, dispatch }) {
    const token = Cookies.get("authToken");
    if (token) {
      console.log("Token found, setting access token");
      commit("setAccessToken", token);
      try {
        await dispatch("fetchUserFromQuickBase");
        await dispatch("scheduleTokenRefresh");
      } catch (error) {
        // If user fetch fails, clear the token and let user re-authenticate
        console.error("Failed to load user from storage:", error);
        commit("clearAccessToken");
        commit("clearQBUserData");
        Cookies.remove("authToken");
      }
    } else {
      console.log("No token found, clearing access token");
      commit("clearAccessToken");
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
